/**
 * Currency Button Component
 * A button that displays the current currency symbol and opens the currency modal
 */

import { useState } from 'react';
import { useUIStore } from '../../store/uiStore';
import { CurrencyModal } from './CurrencyModal';
import {
  type SupportedCurrency,
  CURRENCY_SYMBOLS,
} from '../../types';

interface CurrencyButtonProps {
  currency: SupportedCurrency;
  onCurrencyChange: (currency: SupportedCurrency) => void;
  size?: 'sm' | 'md' | 'lg';
  /** Color variant: 'income' for green (Receitas), 'expense' for orange (Budget) */
  variant?: 'income' | 'expense';
}

export function CurrencyButton({
  currency,
  onCurrencyChange,
  size = 'md',
  variant = 'expense',
}: CurrencyButtonProps) {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sizeClasses = {
    sm: 'text-lg px-2 py-1',
    md: 'text-2xl px-3 py-1.5',
    lg: 'text-3xl px-4 py-2',
  };

  const arrowSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Arrow color based on variant: green for income, orange for expense
  const arrowColorClass = variant === 'income' ? 'text-emerald-500' : 'text-primary-500';

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        data-testid="currency-button"
        className={`font-medium rounded-lg transition-all flex items-center gap-1.5 ${sizeClasses[size]} ${
          isDark
            ? 'text-slate-300 bg-slate-700/30 hover:bg-slate-700/60 hover:text-slate-100'
            : 'text-slate-700 bg-slate-200 hover:bg-slate-300 hover:text-slate-900'
        }`}
      >
        <span>{CURRENCY_SYMBOLS[currency]}</span>
        <svg
          className={`${arrowSizeClasses[size]} ${arrowColorClass}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <CurrencyModal
        isOpen={isModalOpen}
        selectedCurrency={currency}
        onSelect={onCurrencyChange}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
